import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { api, tokenStore, type TelegramAuthPayload } from '../api/client';
import { mountTelegramWidget } from '../auth/telegramWidget';
import { T } from '../styles/tokens';

type Tab = 'login' | 'register';

export default function Auth() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const [tab, setTab] = useState<Tab>((search.get('tab') as Tab) || 'login');
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const tgBoxRef = useRef<HTMLDivElement>(null);

  const botUsername = import.meta.env.VITE_TG_BOT_USERNAME;

  useEffect(() => {
    if (!tgBoxRef.current || !botUsername) return;
    const cleanup = mountTelegramWidget(tgBoxRef.current, botUsername, (payload: TelegramAuthPayload) => {
      setLoading(true);
      setError('');
      api.auth
        .telegram(payload)
        .then((r) => {
          tokenStore.set(r.access, r.refresh);
          nav('/dashboard');
        })
        .catch((e: Error) => setError(e.message))
        .finally(() => setLoading(false));
    });
    return cleanup;
  }, [botUsername, nav]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setError('Заполните все поля');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res =
        tab === 'login'
          ? await api.auth.login(form.email, form.password)
          : await api.auth.register(form.email, form.password, form.name || undefined);
      tokenStore.set(res.access, res.refresh);
      nav('/dashboard');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ошибка';
      if (msg.includes('401')) setError('Неверный email или пароль');
      else if (msg.includes('409')) setError('Этот email уже зарегистрирован');
      else setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={a.root}>
      <div style={a.brand}>
        <div style={a.brandTop}>
          <div style={a.logo} onClick={() => nav('/')}>
            <span style={a.logoBox}>VS</span>
            <span style={a.logoName}>VPN Saler</span>
          </div>
        </div>
        <div style={a.brandBody}>
          <div style={a.brandTag}>// безопасность</div>
          <div style={a.brandTitle}>
            Интернет без
            <br />
            <span style={a.brandAccent}>ограничений</span>
          </div>
          <div style={a.brandPoints}>
            {['WireGuard — скорость', 'VLESS Reality — обход DPI', 'Автовыдача конфига в Telegram', 'Несколько устройств'].map((p) => (
              <div key={p} style={a.brandPoint}>
                <span style={a.brandDash}>—</span>
                {p}
              </div>
            ))}
          </div>
        </div>
        <div style={a.brandWordmark}>VPN</div>
      </div>

      <div style={a.panel}>
        <div style={a.formWrap}>
          <div style={a.formTitle}>{tab === 'login' ? 'Вход в аккаунт' : 'Регистрация'}</div>
          <div style={a.formSub}>{tab === 'login' ? 'Войдите в личный кабинет' : 'Создайте аккаунт за минуту'}</div>

          {botUsername ? (
            <div style={a.tgWidgetBox} ref={tgBoxRef} />
          ) : (
            <div style={a.tgPlaceholder}>
              Telegram Login Widget появится после настройки <code>VITE_TG_BOT_USERNAME</code>
            </div>
          )}

          <div style={a.divider}>
            <div style={a.divLine} />
            <span style={a.divText}>или по email</span>
            <div style={a.divLine} />
          </div>

          <div style={a.tabs}>
            {(['login', 'register'] as Tab[]).map((t) => (
              <button
                key={t}
                style={{ ...a.tab, ...(tab === t ? a.tabActive : {}) }}
                onClick={() => {
                  setTab(t);
                  setError('');
                }}
              >
                {t === 'login' ? 'Войти' : 'Создать аккаунт'}
              </button>
            ))}
          </div>

          <form onSubmit={submit} style={a.form}>
            {tab === 'register' && (
              <div style={a.field}>
                <label style={a.label}>Имя</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ваше имя"
                  style={a.input}
                />
              </div>
            )}
            <div style={a.field}>
              <label style={a.label}>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
                style={a.input}
                autoComplete="email"
              />
            </div>
            <div style={a.field}>
              <label style={a.label}>Пароль</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                style={a.input}
                minLength={8}
                autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
              />
            </div>
            {error && <div style={a.err}>{error}</div>}
            <button type="submit" style={{ ...a.submitBtn, opacity: loading ? 0.65 : 1 }} disabled={loading}>
              {loading ? 'Подождите...' : tab === 'login' ? 'Войти' : 'Зарегистрироваться'}
            </button>
          </form>

          <div style={a.switchRow}>
            <span style={{ color: T.textSec, fontSize: 13 }}>
              {tab === 'login' ? 'Нет аккаунта?' : 'Уже есть аккаунт?'}
            </span>
            <button style={a.switchLink} onClick={() => setTab(tab === 'login' ? 'register' : 'login')}>
              {tab === 'login' ? 'Зарегистрироваться →' : 'Войти →'}
            </button>
          </div>

          <button style={a.backBtn} onClick={() => nav('/')}>
            ← На главную
          </button>
        </div>
      </div>
    </div>
  );
}

const a: Record<string, CSSProperties> = {
  root: { display: 'flex', minHeight: '100vh', background: T.bg, fontFamily: 'DM Sans, sans-serif', color: T.text },
  brand: { width: 420, background: T.surface, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden', flexShrink: 0 },
  brandTop: { padding: '28px 36px' },
  logo: { display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' },
  logoBox: { width: 30, height: 30, background: T.accent, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#000', fontFamily: 'DM Mono, monospace' },
  logoName: { fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 16, letterSpacing: '-0.02em' },
  brandBody: { flex: 1, padding: '0 36px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  brandTag: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.accent, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 20 },
  brandTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 36, letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 36, color: T.text },
  brandAccent: { color: T.accent },
  brandPoints: { display: 'flex', flexDirection: 'column', gap: 12 },
  brandPoint: { display: 'flex', gap: 10, fontSize: 14, color: T.textSec, alignItems: 'center', fontWeight: 500 },
  brandDash: { fontFamily: 'DM Mono, monospace', color: T.accent, fontWeight: 700 },
  brandWordmark: { position: 'absolute', bottom: -30, left: -20, fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 120, color: 'transparent', WebkitTextStroke: `1px ${T.border}`, userSelect: 'none', letterSpacing: '-0.05em', pointerEvents: 'none' },
  panel: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' },
  formWrap: { width: '100%', maxWidth: 400 },
  formTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 26, letterSpacing: '-0.03em', marginBottom: 6, color: T.text },
  formSub: { fontSize: 14, color: T.textSec, marginBottom: 28 },
  tgWidgetBox: { marginBottom: 20, minHeight: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  tgPlaceholder: { border: `1px dashed ${T.border}`, borderRadius: 4, padding: '12px', fontSize: 12, color: T.textMuted, textAlign: 'center', marginBottom: 20, fontFamily: 'DM Mono, monospace' },
  divider: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
  divLine: { flex: 1, height: 1, background: T.border },
  divText: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' },
  tabs: { display: 'flex', border: `1px solid ${T.border}`, borderRadius: 4, overflow: 'hidden', marginBottom: 20 },
  tab: { flex: 1, background: 'transparent', border: 'none', color: T.textSec, fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '10px 0', transition: 'all 0.15s' },
  tabActive: { background: T.surfaceUp, color: T.text },
  form: { display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' },
  input: { background: T.surfaceUp, border: `1px solid ${T.border}`, borderRadius: 4, padding: '12px 14px', color: T.text, fontSize: 14, fontFamily: 'DM Sans, sans-serif', outline: 'none', width: '100%', boxSizing: 'border-box' },
  err: { background: 'oklch(0.68 0.2 25 / 0.1)', border: '1px solid oklch(0.68 0.2 25 / 0.3)', borderRadius: 3, padding: '10px 12px', fontSize: 13, color: 'oklch(0.68 0.2 25)' },
  submitBtn: { background: T.accent, border: 'none', borderRadius: 4, padding: '13px', color: '#000', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  switchRow: { display: 'flex', gap: 6, alignItems: 'center', marginBottom: 16 },
  switchLink: { background: 'none', border: 'none', color: T.accent, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  backBtn: { background: 'none', border: 'none', color: T.textMuted, fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', padding: 0 },
};
